import { generateClient } from "aws-amplify/api";
import { list } from 'aws-amplify/storage';
import { AUTH_MODE, DEFAULT_SENSOR_STATUS, ITEM_NUM_PER_PAGE } from 'constants/config';
import { TEXT_ERROR_SENSOR_ALREADY_EXISTED } from "constants/message";
import { LockState } from "models";
import moment from 'moment';
import { convertDateTimeFormat, convertMomentFormat, sortFileByName } from "utils/function";
import { v4 as uuidv4 } from 'uuid';
import { listSensorGroups } from "../graphql/customQueries";
import { createSensor, createSensorGroup, deleteCameraSensorHistory, deleteSensor, deleteSensorGroup, handleImageAnalysis, handleResetUserDeviceGroups, updateSensor, updateSensorGroup } from "../graphql/mutations";
import { getSensor, listCameraSensorHistories, listSensorChangeHistorys, listSensorHistorys, listSensors, queryBySensorIdIsAnalyzed } from "../graphql/queries";
import { onCreateSensor, onCreateSensorGroup, onDeleteSensor, onDeleteSensorGroup, onUpdateSensor, onUpdateSensorGroup } from "../graphql/subscriptions";


const client = generateClient();

const apiListGroups = async () => {
  try {
    const params = {
      query: listSensorGroups,
      authMode: AUTH_MODE
    }
    let nextToken = null;
    const groups = [];
    while (true) {
      params.nextToken = nextToken;
      const res = await client.graphql(params);
      groups.push(...res.data?.listSensorGroups?.items);
      nextToken = res.data?.listSensorGroups?.nextToken;
      if (!nextToken) break;
    }
    for (const group of groups) {
      group.createdAt = convertDateTimeFormat(group.createdAt);
      group.updatedAt = convertDateTimeFormat(group.updatedAt);
    }
    groups.sort((g1, g2) => -g1.createdAt.localeCompare(g2.createdAt));
    return {
      success: true,
      data: groups
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiCreateGroup = async (groupData) => {
  try {
    const res = await client.graphql({
      query: createSensorGroup,
      variables: { input: groupData },
      authMode: AUTH_MODE
    })
    const group = res.data?.createSensorGroup;
    group.createdAt = convertDateTimeFormat(group.createdAt);
    group.updatedAt = convertDateTimeFormat(group.updatedAt);
    return {
      success: true,
      data: group
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiUpdateGroup = async (groupData) => {
  try {
    const res = await client.graphql({
      query: updateSensorGroup,
      variables: { input: groupData },
      authMode: AUTH_MODE
    })
    const group = res.data?.updateSensorGroup;
    group.createdAt = convertDateTimeFormat(group.createdAt);
    group.updatedAt = convertDateTimeFormat(group.updatedAt);
    return {
      success: true,
      data: group
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiDeleteGroup = async (groupId) => {
  try {
    const res = await client.graphql({
      query: deleteSensorGroup,
      variables: { input: { id: groupId } },
      authMode: AUTH_MODE
    })
    const data = res.data?.deleteSensorGroup;
    return {
      success: true,
      data: data
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiListSensors = async () => {
  try {
    let nextToken = null;
    const data = [];
    while (true) {
      const response = await client.graphql({
        query: listSensors,
        authMode: AUTH_MODE,
        variables: {
          nextToken
        }
      });
      data.push(...response?.data?.listSensors?.items);
      nextToken = response?.data?.listSensors?.nextToken;
      if (!nextToken) break;
    }

    for (const sensor of data) {
      sensor.tx = convertMomentFormat(sensor.tx);
      sensor.createdAt = convertDateTimeFormat(sensor.createdAt);
      sensor.updatedAt = convertMomentFormat(sensor.updatedAt);
      if (sensor.data) {
        sensor.data = JSON.parse(sensor.data);
      }
    }
    return {
      success: true,
      data: {
        items: data
      }
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiCreateSensor = async (data) => {
  try {
    if (data.data) data.data = JSON.stringify(data.data);
    // if this is sensor
    if (data.id && data.id !== 'camera' && data.id !== 'socket') {
      const variables = {
        filter: {
          id: {
            beginsWith: data.id
          }
        }
      };
      let nextToken = null;
      let items = null;
      while (true) {
        variables.nextToken = nextToken;
        const response = await client.graphql({
          query: listSensors,
          variables,
          authMode: AUTH_MODE
        });
        items = response?.data?.listSensors?.items;
        nextToken = response?.data?.listSensors?.nextToken;
        if (!nextToken || !items || items.length) break;
      }
      if (items?.length) {
        return {
          success: false,
          error: TEXT_ERROR_SENSOR_ALREADY_EXISTED
        }
      }
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
  data.status = DEFAULT_SENSOR_STATUS;
  data.isLocked = LockState.NOLOCK;
  // if this is sensor
  if (data.id) {
    data.id = `${data.id}+${uuidv4()}`;
  }
  // in case of camera, ignore id to generate id randomly
  try {
    const response = await client.graphql({
      query: createSensor,
      variables: { input: data },
      authMode: AUTH_MODE
    });
    const sensor = response.data?.createSensor;
    sensor.createdAt = convertDateTimeFormat(sensor.createdAt);
    sensor.updatedAt = convertMomentFormat(sensor.updatedAt);
    sensor.tx = "";
    return {
      success: true,
      data: sensor
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiUpdateSensor = async (data) => {
  try {
    if (data.data) data.data = JSON.stringify(data.data);
    const res = await client.graphql({
      query: updateSensor,
      variables: { input: data },
      authMode: AUTH_MODE
    });
    const sensor = res.data?.updateSensor;
    sensor.createdAt = convertDateTimeFormat(sensor.createdAt);
    sensor.updatedAt = convertMomentFormat(sensor.updatedAt);
    sensor.tx = convertMomentFormat(sensor.tx);
    return {
      success: true,
      data: sensor
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiDeleteSensor = async (sensorId) => {
  try {
    const res = await client.graphql({
      query: deleteSensor,
      variables: { input: { id: sensorId } },
      authMode: AUTH_MODE
    });
    return {
      success: true,
      data: res.data?.deleteSensor
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

/**
 * Fetches a sensor by its ID.
 * @param {string} sensorId The ID of the sensor to fetch.
 * @returns {Promise<{success: boolean, data?: any, error?: any}>}
 */
const apiGetSensor = async (sensorId) => {
  try {
    const res = await client.graphql({
      query: getSensor,
      variables: { id: sensorId },
      authMode: AUTH_MODE
    });
    const sensor = res.data?.getSensor;
    if (sensor) {
      sensor.createdAt = convertDateTimeFormat(sensor.createdAt);
      sensor.updatedAt = convertMomentFormat(sensor.updatedAt);
      sensor.tx = convertMomentFormat(sensor.tx);
      if (sensor.data) {
        sensor.data = JSON.parse(sensor.data);
      }
    }
    return {
      success: true,
      data: sensor
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiListHistories = async (sensorId, nextToken = null, createdAt = {}, limit = ITEM_NUM_PER_PAGE) => {
  try {
    const res = await client.graphql({
      query: listSensorChangeHistorys,
      variables: {
        sensorId,
        createdAt,
        nextToken,
        sortDirection: 'DESC',
        limit,
      },
      authMode: AUTH_MODE
    });
    for (const history of res.data?.listSensorChangeHistorys.items) {
      history.createdAt = convertDateTimeFormat(history.createdAt);
      history.updatedAt = convertMomentFormat(history.updatedAt);
      history.tx = convertMomentFormat(history.tx);
      if (history.data) {
        history.data = JSON.parse(history.data);
      }
    }
    return {
      success: true,
      data: res.data?.listSensorChangeHistorys.items,
      nextToken: res.data?.listSensorChangeHistorys.nextToken
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiAllListHistories = async (groupId, startTime, endTime) => {
  try {
    let nextToken = null;
    const sensors = [];
    while (true) {
      const res = await client.graphql({
        query: listSensors,
        variables: {
          nextToken,
          filter: {
            groupId: { eq: groupId }
          }
        },
        authMode: AUTH_MODE
      });
      nextToken = res.data.listSensors.nextToken;
      sensors.push(...res.data.listSensors.items);
      if (!nextToken) break;
    }
    nextToken = null;
    const data = [];
    for (const sensor of sensors) {
      while (true) {
        const res = await client.graphql({
          query: listSensorHistorys,
          variables: {
            sensorId: sensor.id,
            createdAt: {
              between: [startTime, endTime]
            },
            nextToken,
            sortDirection: 'DESC',
            // limit: 1000
          },
          authMode: AUTH_MODE
        });
        nextToken = res.data.listSensorHistorys.nextToken;
        data.push(...res.data.listSensorHistorys.items);
        if (!nextToken) break;
      }
    }
    for (const item of data) {
      const sensor = sensors.find(s => s.id === item.sensorId);
      item.groupId = sensor.groupId;
      item.sensorName = sensor.name;
    }
    return {
      success: true,
      data: data
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiListCameraSensorHistories = async (sensorId, nextToken = null, tx, limit = ITEM_NUM_PER_PAGE) => {
  try {
    const res = await client.graphql({
      query: listCameraSensorHistories,
      variables: {
        sensorId,
        tx,
        limit,
        nextToken,
        sortDirection: 'DESC',
      },
      authMode: AUTH_MODE
    });
    return {
      success: true,
      data: res.data?.listCameraSensorHistories.items,
      nextToken: res.data?.listCameraSensorHistories.nextToken
    }
  } catch (error) {
    return {
      success: false,
      error
    }
  }
}

const apiListAnalyzedCameraSensorHistories = async (sensorId, nextToken = null, limit = ITEM_NUM_PER_PAGE) => {
  try {
    const res = await client.graphql({
      query: queryBySensorIdIsAnalyzed,
      variables: {
        sensorId,
        isAnalyzedTx: { beginsWith: { isAnalyzed: '1' } },
        limit,
        nextToken,
        sortDirection: 'DESC'
      },
      authMode: AUTH_MODE
    });
    for (const history of res.data?.queryBySensorIdIsAnalyzed.items) {
      history.createdAt = convertDateTimeFormat(history.createdAt);
      history.updatedAt = convertMomentFormat(history.updatedAt);
      history.tx = convertMomentFormat(history.tx);
      if (history.data) {
        history.data = JSON.parse(history.data);
      }
    }
    return {
      success: true,
      data: res.data?.queryBySensorIdIsAnalyzed.items,
      nextToken: res.data?.queryBySensorIdIsAnalyzed.nextToken
    }
  } catch (error) {
    return {
      success: false,
      error
    }
  }
}
const apiListCameraSensorHistories6hAgo = async (sensorId, cameraNumber = 1) => {
  try {
    let nextToken = null;
    const data = [];
    const startTime = moment().subtract(6, 'hours').toDate()
    const endTime = moment().toDate()
    while (true) {
      const res = await client.graphql({
        query: listCameraSensorHistories,
        variables: {
          sensorId: `${sensorId}_${cameraNumber}`,
          tx: {
            between: [startTime.toISOString(), endTime.toISOString()]
          },
          nextToken,
          sortDirection: 'ASC',
        },
        authMode: AUTH_MODE
      });
      nextToken = res.data.listCameraSensorHistories.nextToken;
      data.push(...res.data.listCameraSensorHistories.items);
      if (!nextToken) break;
    }
    return {
      success: true,
      data: data
    }
  } catch (error) {

  }
}
const apiDeleteCameraSensorHistory = async (sensorId, tx) => {
  try {
    const res = await client.graphql({
      query: deleteCameraSensorHistory,
      variables: { input: { sensorId, tx } },
      authMode: AUTH_MODE
    });
    return {
      success: true,
      data: res.data?.deleteCameraSensorHistory
    }
  } catch (error) {
    return {
      success: false,
      error
    }
  }
}

const apiAddCreateSensorListener = async (handler) => {
  try {
    const listener = await client.graphql({
      query: onCreateSensor,
      authMode: AUTH_MODE
    }).subscribe({
      next: ({ data }) => {
        const newSensor = data.onCreateSensor;
        newSensor.createdAt = convertDateTimeFormat(newSensor.createdAt);
        newSensor.updatedAt = convertMomentFormat(newSensor.updatedAt);
        newSensor.tx = "";
        if (newSensor.data) {
          newSensor.data = JSON.parse(newSensor.data);
        }
        handler(newSensor);
      },
      error: (error) => console.error(error)
    });
    return {
      success: true,
      data: listener
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiAddUpdateSensorListener = async (handler) => {
  try {
    const listener = await client.graphql({
      query: onUpdateSensor,
      authMode: AUTH_MODE
    }).subscribe({
      next: ({ data }) => {
        const newSensor = data.onUpdateSensor;
        newSensor.createdAt = convertDateTimeFormat(newSensor.createdAt);
        newSensor.updatedAt = convertMomentFormat(newSensor.updatedAt);
        newSensor.tx = convertMomentFormat(newSensor.tx);
        if (newSensor.data) {
          newSensor.data = JSON.parse(newSensor.data);
        }
        handler(newSensor);
      },
      error: (error) => console.error(error)
    });
    return {
      success: true,
      data: listener
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiAddDeleteSensorListener = async (handler) => {
  try {
    const listener = await client.graphql({
      query: onDeleteSensor,
      authMode: AUTH_MODE
    }).subscribe({
      next: ({ data }) => {
        handler(data.onDeleteSensor);
      },
      error: (error) => console.error(error)
    });
    return {
      success: true,
      data: listener
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiAddCreateGroupListener = async (handler) => {
  try {
    const listener = await client.graphql({
      query: onCreateSensorGroup,
      authMode: AUTH_MODE
    }).subscribe({
      next: ({ data }) => {
        const newGroup = data.onCreateSensorGroup;
        newGroup.createdAt = convertDateTimeFormat(newGroup.createdAt);
        newGroup.updatedAt = convertDateTimeFormat(newGroup.updatedAt);
        handler(newGroup);
      },
      error: (error) => console.error(error)
    });
    return {
      success: true,
      data: listener
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiAddUpdateGroupListener = async (handler) => {
  try {
    const listener = await client.graphql({
      query: onUpdateSensorGroup,
      authMode: AUTH_MODE
    }).subscribe({
      next: ({ data }) => {
        const newGroup = data.onUpdateSensorGroup;
        newGroup.createdAt = convertDateTimeFormat(newGroup.createdAt);
        newGroup.updatedAt = convertDateTimeFormat(newGroup.updatedAt);
        handler(newGroup);
      },
      error: (error) => console.error(error)
    });
    return {
      success: true,
      data: listener
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiAddDeleteGroupListener = async (handler) => {
  try {
    const listener = await client.graphql({
      query: onDeleteSensorGroup,
      authMode: AUTH_MODE
    }).subscribe({
      next: ({ data }) => {
        handler(data.onDeleteSensorGroup);
      },
      error: (error) => console.error(error)
    });
    return {
      success: true,
      data: listener
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiRemoveListener = (listener) => {
  if (listener.unsubscribe) listener.unsubscribe();
}

const apiList3LastDayHistories = async (sensorId) => {
  const startTime = new Date();
  startTime.setDate(startTime.getDate() - 3);
  try {
    let nextToken = null;
    const sensors = [];
    while (true) {
      const res = await client.graphql({
        query: listSensorHistorys,
        variables: {
          sensorId: sensorId,
          createdAt: {
            ge: startTime.toISOString()
          },
          filter: {
            tx: {
              ge: startTime.toISOString()
            }
          },
          sortDirection: 'DESC',
          nextToken
        },
        authMode: AUTH_MODE
      });
      nextToken = res.data.listSensorHistorys.nextToken;
      sensors.push(...res.data.listSensorHistorys.items);
      if (!nextToken) break;
    }
    for (const sensor of sensors) {
      if (sensor.data) {
        sensor.data = JSON.parse(sensor.data);
      }
    }
    return {
      success: true,
      data: sensors
    }
  }
  catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiListCameraImagePastFlood = async (sensorId, cameraNumber = 1) => {
  try {
    const prefix = `camera/past-floods+${sensorId}/${cameraNumber}_`
    const response = await list({
      prefix,
      options: {
        listAll: true
      }
    });
    const sortedData = sortFileByName(response.items || [])
    return {
      data: sortedData,
      success: true
    }
  } catch (e) {
    return {
      success: false,
      error: e
    }
  }
}

const apiImageAnalysis = async (input) => {
  try {
    const response = await client.graphql({
      query: handleImageAnalysis,
      variables: {
        input
      },
      authMode: AUTH_MODE
    })
    return response.data.handleImageAnalysis
  } catch (error) {
    return {
      success: false,
      error
    }
  }
}

const apiResetUserDeviceGroups = async (cameraId, newDirections) => {
  try {
    const response = await client.graphql({
      query: handleResetUserDeviceGroups,
      variables: {
        input: {
          cameraId,
          newDirections
        }
      },
      authMode: AUTH_MODE
    })
    return response.data.handleResetUserDeviceGroups
  } catch (error) {
    return {
      success: false,
      error
    }
  }
}

export {
  apiAddCreateGroupListener,
  apiAddCreateSensorListener,
  apiAddDeleteGroupListener,
  apiAddDeleteSensorListener,
  apiAddUpdateGroupListener,
  apiAddUpdateSensorListener,
  apiAllListHistories,
  apiCreateGroup,
  apiCreateSensor,
  apiDeleteGroup,
  apiDeleteSensor,
  apiGetSensor,
  apiListSensors,
  apiListGroups,
  apiListHistories,
  apiList3LastDayHistories,
  apiListCameraSensorHistories,
  apiListAnalyzedCameraSensorHistories,
  apiListCameraSensorHistories6hAgo,
  apiListCameraImagePastFlood,
  apiDeleteCameraSensorHistory,
  apiRemoveListener,
  apiUpdateGroup,
  apiUpdateSensor,
  apiImageAnalysis,
  apiResetUserDeviceGroups
};
